import type { BuilderMessage } from "@/shared/builder-session";
import {
  abortActiveWorkflowStream,
  emitChatsChanged,
  isWorkflowStreamActive,
  onChatsChanged,
} from "@/shared/builder-session";
import { sanitizePublicTranscript } from "@/features/chat/multi/contract/message-contract";
import {
  clearLiveBuffer,
  getLiveBuffer,
  resetLiveStoreForTests,
  setLiveBuffer,
} from "@/features/chat/multi/store/chat-live-store";
import {
  isChatAwaitingInput,
  isChatStreaming,
  getFocusedSession,
  setChatAwaitingInput,
  setChatStreaming,
} from "@/features/chat/multi/signals/chat-signals";
import {
  deleteBuildChat,
  flushBuildChatWrites,
  getCachedBuildChat,
  pruneBuildChatKeys,
  readBuildChat,
  resetBuildChatStorageForTests,
  writeBuildChat,
} from "@/features/chat/multi/history/build-chat-storage";
import { flowopsFetch } from "@/shared/builder-session/flowops-api-url";

const INDEX_KEY = "flow-builder-chat-index";
const CHAT_PREFIX = "flow-builder-chat:";
/** Legacy localStorage blob cap (pre-IndexedDB). IDB keeps the full transcript. */
const MAX_STORED_MESSAGES = 200;
const SAVE_DEBOUNCE_MS = 1000;
const MAX_SUMMARY_CHARS = 240;

export interface ChatHistoryItem {
  id: string;
  title: string;
  updatedAt: string;
  pinned?: boolean;
  agentName?: string;
  workflowId?: string;
  /** Set when the user explicitly renames — auto titles / agent names must not override. */
  titleLocked?: boolean;
}

/** Debounced disk writes for the live SSE buffer only. */
const saveTimers = new Map<string, number>();

export function chatStorageKey(sessionId: string) {
  return `${CHAT_PREFIX}${sessionId.trim()}`;
}

function isPlaceholder(value?: string | null) {
  const text = value?.trim() ?? "";
  if (!text) return true;
  // Match v2-frontend generic sidebar titles (+ our draft defaults).
  return /^(new chat|new draft|chat|new automation|untitled(\s+workflow)?)$/i.test(text);
}

/** True for generic labels that should not stick as a real draft/agent name. */
export function isPlaceholderChatTitle(value?: string | null) {
  return isPlaceholder(value);
}

function stripLive(item: ChatHistoryItem): ChatHistoryItem {
  // Older localStorage entries may still have live/awaiting flags — never surface them.
  const {
    live: _ignored,
    awaitingInput: _awaiting,
    runActive: _run,
    ...rest
  } = item as ChatHistoryItem & {
    live?: boolean;
    awaitingInput?: boolean;
    runActive?: boolean;
  };
  return rest;
}

function readIndex(): ChatHistoryItem[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(INDEX_KEY) || "[]") as ChatHistoryItem[];
    return Array.isArray(parsed) ? parsed.filter((item) => item?.id).map(stripLive) : [];
  } catch {
    return [];
  }
}

/** Best-effort localStorage write — never throws into stream handlers. */
export function safeSetItem(key: string, value: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    reclaimLocalStorageSpace(key);
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch {
      // Last resort: wipe every flow-builder chat blob and try once more.
      wipeAllChatTranscripts(key);
      try {
        window.localStorage.setItem(key, value);
        return true;
      } catch {
        console.warn("[chat-history] Storage full, skipping persistence");
        return false;
      }
    }
  }
}

/** Drop chat transcript blobs so tiny keys (session pointer) can still write. */
function reclaimLocalStorageSpace(protectKey: string) {
  if (typeof window === "undefined") return;
  const keys: string[] = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (key?.startsWith(CHAT_PREFIX) && key !== protectKey) keys.push(key);
  }
  // Oldest-looking keys first (uuid order is weak; prefer index order).
  const indexIds = new Set(readIndex().map((item) => item.id));
  keys.sort((a, b) => {
    const idA = a.slice(CHAT_PREFIX.length);
    const idB = b.slice(CHAT_PREFIX.length);
    const inA = indexIds.has(idA) ? 0 : 1;
    const inB = indexIds.has(idB) ? 0 : 1;
    return inA - inB || a.localeCompare(b);
  });

  for (const key of keys) {
    try {
      // Disk only — never wipe RAM buffers for other chats (same-tab switch relies on them).
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}

function wipeAllChatTranscripts(protectKey: string) {
  if (typeof window === "undefined") return;
  const keys: string[] = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (key?.startsWith(CHAT_PREFIX) && key !== protectKey) keys.push(key);
  }
  for (const key of keys) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}

function writeIndex(items: ChatHistoryItem[]) {
  if (typeof window === "undefined") return;
  const serialized = JSON.stringify(items.slice(0, 200).map(stripLive));
  if (window.localStorage.getItem(INDEX_KEY) === serialized) return;
  if (!safeSetItem(INDEX_KEY, serialized)) return;
  emitChatsChanged();
}

/** Strip heavy tool payloads before disk — keep labels for the transcript UI.
 * Plans keep their steps (small text); stripping them emptied the plan card on restore. */
export function compactMessagesForStorage(messages: BuilderMessage[]): BuilderMessage[] {
  return sanitizePublicTranscript(messages)
    .slice(-MAX_STORED_MESSAGES)
    .map((message) => {
      if (message.type !== "tool") {
        return message;
      }
      const details =
        message.details && typeof message.details === "object"
          ? (message.details as Record<string, unknown>)
          : undefined;
      const summaryRaw = details?.summary;
      const summary =
        typeof summaryRaw === "string" ? summaryRaw.slice(0, MAX_SUMMARY_CHARS) : undefined;
      return {
        ...message,
        details: details
          ? {
              tool: details.tool,
              phase: details.phase ?? (/^Calling\s/i.test(message.text) ? "call" : "result"),
              ...(summary ? { summary } : {}),
            }
          : undefined,
      };
    });
}

/** FlowOps IndexedDB write — full bubbles (tool results included). */
function writeMessagesToDisk(id: string, messages: BuilderMessage[]) {
  writeBuildChat(id, sanitizePublicTranscript(messages));
  // Drop legacy localStorage blob once IDB owns the transcript.
  try {
    window.localStorage.removeItem(chatStorageKey(id));
  } catch {
    // ignore
  }
}

function scheduleDiskWrite(id: string, messages: BuilderMessage[]) {
  const existing = saveTimers.get(id);
  if (existing !== undefined) window.clearTimeout(existing);
  const timer = window.setTimeout(() => {
    saveTimers.delete(id);
    // Prefer latest memory snapshot if appends landed after schedule.
    writeMessagesToDisk(id, getLiveBuffer(id).length ? getLiveBuffer(id) : messages);
  }, SAVE_DEBOUNCE_MS);
  saveTimers.set(id, timer);
}

function migrateLegacyLocalStorage(id: string): BuilderMessage[] {
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(chatStorageKey(id)) || "[]",
    ) as BuilderMessage[];
    const messages = Array.isArray(parsed) ? parsed : [];
    if (!messages.length) return [];
    writeBuildChat(id, sanitizePublicTranscript(messages));
    window.localStorage.removeItem(chatStorageKey(id));
    return messages;
  } catch {
    return [];
  }
}

/** v2-frontend: first real user message, max 40 chars. */
function titleFromMessages(messages: BuilderMessage[], fallback?: string) {
  const user = messages.find((message) => message.type === "user" && message.text.trim());
  return (user?.text.trim() || fallback?.trim() || "New chat").replace(/\s+/g, " ").slice(0, 40);
}

/** Sidebar label: real agent/workflow name if set, otherwise chat title (v2 pattern). */
export function displayChatTitle(item: ChatHistoryItem): string {
  if (item.titleLocked && item.title.trim()) return item.title.slice(0, 80);
  if (item.agentName && !isPlaceholder(item.agentName)) return item.agentName.slice(0, 40);
  if (item.title && !isPlaceholder(item.title)) return item.title.slice(0, 40);
  return (item.title || "New chat").slice(0, 40);
}

/** Live sidebar/workspace label for an open session, or "" if unknown. */
export function chatDisplayTitleForSession(sessionId?: string | null): string {
  const id = sessionId?.trim();
  if (!id) return "";
  const item = listChatHistory().find((chat) => chat.id === id);
  return item ? displayChatTitle(item) : "";
}

export function listChatHistory(): ChatHistoryItem[] {
  return sortChatHistory(readIndex());
}

function sortChatHistory(items: ChatHistoryItem[]): ChatHistoryItem[] {
  return [...items].sort((a, b) => {
    if (Boolean(a.pinned) !== Boolean(b.pinned)) return a.pinned ? -1 : 1;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

/** Refresh the local sidebar cache from the durable FlowOps session index. */
export async function hydrateChatHistoryFromServer(): Promise<ChatHistoryItem[]> {
  if (typeof window === "undefined") return [];
  const existing = readIndex();
  let response: Response | null = null;
  let lastError: unknown;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      response = await flowopsFetch("/api/workflow/sessions", {
        cache: "no-store",
        signal: AbortSignal.timeout(45_000),
      });
      if (response.ok) break;
      lastError = new Error(`sessions ${response.status}`);
    } catch (error) {
      lastError = error;
      response = null;
    }
    if (attempt < 2) {
      await new Promise((resolve) => window.setTimeout(resolve, 400 * (attempt + 1)));
    }
  }

  if (!response?.ok) {
    if (lastError) {
      console.warn("[chat-history] Failed to hydrate sessions from server", lastError);
    }
    // Keep whatever is already cached — never wipe the sidebar on a transient failure.
    return existing;
  }

  const payload = (await response.json()) as {
    chats?: Array<ChatHistoryItem & { runActive?: boolean; awaitingInput?: boolean }>;
  };
  const remote = Array.isArray(payload.chats)
    ? payload.chats.map(({ runActive, awaitingInput, ...item }) => {
        setChatLive(item.id, Boolean(runActive));
        // Background: list may ARM yellow when the server says parked — never CLEAR
        // it (a stale list pull was wiping SSE-armed Needs input until the chat opened).
        // Focused chat: restore / card paint own yellow.
        if (item.id !== getFocusedSession()) {
          if (awaitingInput) setChatAwaitingInput(item.id, true);
        } else if (!awaitingInput) {
          setChatAwaitingInput(item.id, false);
        }
        return stripLive(item);
      })
    : [];
  const remoteIds = new Set(remote.map((item) => item.id));
  const localById = new Map(existing.map((item) => [item.id, item]));
  // Keep chats that just appeared locally (user sent, switched away) before Mongo lists them.
  const localOnly = existing.filter(
    (item) => item?.id && !remoteIds.has(item.id) && isChatLive(item.id),
  );
  const merged = sortChatHistory(
    [
      ...remote.map((item) => {
        const local = localById.get(item.id);
        if (!local?.titleLocked) return item;
        return {
          ...item,
          title: local.title,
          titleLocked: true,
          agentName: undefined,
        };
      }),
      ...localOnly,
    ].filter((item) => item?.id),
  ).slice(0, 200);
  writeIndex(merged);
  pruneOrphanDiskCaches(merged.map((item) => item.id));
  return merged;
}

/** Most recent builder chat linked to a saved workflow (for continue building). */
export function findLatestChatForWorkflow(workflowId: string): ChatHistoryItem | null {
  const id = workflowId.trim();
  if (!id) return null;
  const matches = readIndex().filter((item) => item.workflowId === id);
  if (!matches.length) return null;
  return matches.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  )[0]!;
}

export function getChatMessages(sessionId: string): BuilderMessage[] {
  if (typeof window === "undefined") return [];
  const id = sessionId.trim();
  if (!id) return [];
  return getLiveBuffer(id);
}

/**
 * Sync crash-recovery buffer. Prefers RAM → IndexedDB cache → legacy localStorage.
 * Call `awaitPersistedLiveBuffer` first on cold restore so IndexedDB is warm.
 */
export function readPersistedLiveBuffer(sessionId: string): BuilderMessage[] {
  if (typeof window === "undefined") return [];
  const id = sessionId.trim();
  if (!id) return [];
  const live = getLiveBuffer(id);
  if (live.length) return live;
  const cached = getCachedBuildChat(id);
  if (cached?.length) {
    setLiveBuffer(id, cached);
    return cached;
  }
  const migrated = migrateLegacyLocalStorage(id);
  if (migrated.length) {
    setLiveBuffer(id, migrated);
    return migrated;
  }
  return [];
}

/** Warm IndexedDB into the sync cache, then return the crash-recovery buffer. */
export async function awaitPersistedLiveBuffer(sessionId: string): Promise<BuilderMessage[]> {
  const id = sessionId.trim();
  if (!id) return [];
  const live = getLiveBuffer(id);
  if (live.length) return live;
  try {
    const fromIdb = await readBuildChat(id);
    if (fromIdb.length) {
      setLiveBuffer(id, fromIdb);
      return fromIdb;
    }
  } catch {
    // fall through to legacy localStorage
  }
  return readPersistedLiveBuffer(id);
}

export function clearDiskLiveBuffer(sessionId: string) {
  if (typeof window === "undefined") return;
  const id = sessionId.trim();
  if (!id) return;
  const timer = saveTimers.get(id);
  if (timer !== undefined) {
    window.clearTimeout(timer);
    saveTimers.delete(id);
  }
  deleteBuildChat(id);
  try {
    window.localStorage.removeItem(chatStorageKey(id));
  } catch {
    // ignore
  }
}

/**
 * Update the in-memory transcript immediately.
 * Disk is the FlowOps IndexedDB stand-in until session PATCH/uiMessages exists —
 * keep it across idle parks so hard refresh restores interaction order.
 */
export function saveChatMessages(
  sessionId: string,
  messages: BuilderMessage[],
  options?: { flush?: boolean },
) {
  if (typeof window === "undefined") return;
  const id = sessionId.trim();
  if (!id) return;
  setLiveBuffer(id, messages);

  // Soft HITL park must sync even if a durable hub still looks "live".
  const awaiting = isChatAwaitingInput(id);
  const live = !awaiting && (isChatStreaming(id) || isWorkflowStreamActive(id));
  if (!live) {
    if (options?.flush) {
      // FlowOps IndexedDB parity — persist locally (server PATCH is GET-only / 405).
      syncUiTranscriptToServer(id, messages, { flush: true });
    } else {
      scheduleDiskWrite(id, messages);
    }
    return;
  }

  if (options?.flush) {
    const timer = saveTimers.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      saveTimers.delete(id);
    }
    writeMessagesToDisk(id, messages);
    return;
  }
  scheduleDiskWrite(id, messages);
}

/**
 * Persist the painted BuilderMessage timeline for hard refresh.
 * Session route is GET-only (old FlowOps never PATCHed) — write IndexedDB
 * like FlowOps `build-chat-storage` (full bubbles).
 */
export function syncUiTranscriptToServer(
  sessionId: string,
  messages: BuilderMessage[],
  options?: { flush?: boolean },
) {
  if (typeof window === "undefined") return;
  const id = sessionId.trim();
  if (!id) return;
  const cleaned = sanitizePublicTranscript(messages);
  if (!cleaned.length) return;
  // Never upload a card-only slice — that poisons restore and hides Memory text.
  if (!cleaned.some((message) => message.type === "user" && message.text.trim())) {
    return;
  }
  void options;
  writeMessagesToDisk(id, cleaned);
}

/**
 * After a successful server hydrate with durable uiMessages, disk can drop.
 * Until the API returns uiMessages, keep the local buffer (IndexedDB parity).
 */
export function releaseIdleDiskCache(sessionId: string) {
  if (typeof window === "undefined") return;
  const id = sessionId.trim();
  if (!id) return;
  if (isChatStreaming(id) || isWorkflowStreamActive(id)) return;
  // No server uiMessages yet — do not clear the local transcript.
}

/** Drop disk blobs for chats that are neither on the server index nor live. */
export function pruneOrphanDiskCaches(keepSessionIds: Iterable<string>) {
  if (typeof window === "undefined") return;
  const keep = new Set([...keepSessionIds].map((id) => id.trim()).filter(Boolean));
  const remove: string[] = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (!key?.startsWith(CHAT_PREFIX)) continue;
    const id = key.slice(CHAT_PREFIX.length);
    if (!id || keep.has(id)) continue;
    if (isChatStreaming(id) || isWorkflowStreamActive(id) || isChatLive(id)) continue;
    remove.push(key);
  }
  for (const key of remove) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
  pruneBuildChatKeys(keep);
}

/** Force pending debounced writes — idle / HITL-park keeps IndexedDB (FlowOps parity). */
export function flushChatMessages(sessionId?: string) {
  if (typeof window === "undefined") return;
  const flushOne = (id: string) => {
    const timer = saveTimers.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      saveTimers.delete(id);
    }
    const messages = getLiveBuffer(id);
    const awaiting = isChatAwaitingInput(id);
    const live = !awaiting && (isChatStreaming(id) || isWorkflowStreamActive(id));
    if (!live) {
      if (messages.length) syncUiTranscriptToServer(id, messages, { flush: true });
      return;
    }
    if (messages.length) writeMessagesToDisk(id, messages);
  };
  if (sessionId?.trim()) {
    flushOne(sessionId.trim());
    flushBuildChatWrites();
    return;
  }
  for (const id of [...saveTimers.keys()]) flushOne(id);
  flushBuildChatWrites();
}

/**
 * Update sidebar metadata in place.
 * Never moves the chat — opening / restoring / graph load must use this.
 */
export function patchChatHistory(
  sessionId: string,
  patch: {
    title?: string;
    agentName?: string | null;
    workflowId?: string | null;
    messages?: BuilderMessage[];
    fallbackTitle?: string;
  },
) {
  if (typeof window === "undefined") return;
  const id = sessionId.trim();
  if (!id) return;

  const index = readIndex();
  const existing = index.find((item) => item.id === id);
  if (!existing) return;

  if (existing.titleLocked) {
    const workflowId = patch.workflowId?.trim() || existing.workflowId;
    writeIndex(
      index.map((item) =>
        item.id === id
          ? {
              ...item,
              ...(workflowId ? { workflowId } : {}),
            }
          : item,
      ),
    );
    return;
  }

  const fromMessages =
    patch.messages && patch.messages.length
      ? titleFromMessages(patch.messages, patch.fallbackTitle)
      : undefined;
  // v2: only replace generic titles — keep user/agent renames.
  const nextTitle =
    fromMessages && !isPlaceholder(fromMessages) && isPlaceholder(existing.title)
      ? fromMessages
      : patch.title && !isPlaceholder(patch.title) && isPlaceholder(existing.title)
        ? patch.title
        : existing.title || fromMessages || "New chat";

  // Only apply agentName when explicitly provided (saved workflow / finalize).
  // Omitting it keeps the existing value — do not push early "first 6 words" drafts.
  const agentName =
    patch.agentName === null
      ? undefined
      : patch.agentName != null && !isPlaceholder(patch.agentName)
        ? patch.agentName.trim()
        : existing.agentName;
  const workflowId = patch.workflowId?.trim() || existing.workflowId;

  writeIndex(
    index.map((item) =>
      item.id === id
        ? {
            ...item,
            title: nextTitle,
            ...(agentName ? { agentName } : {}),
            ...(workflowId ? { workflowId } : {}),
          }
        : item,
    ),
  );
}

/**
 * Ensure the chat is in the sidebar index and emit chats-changed.
 * Safe to call from foreground or background when a session id is first known.
 */
export function ensureChatListed(options: {
  sessionId: string;
  messages?: BuilderMessage[];
  fallbackTitle?: string;
  agentName?: string | null;
  workflowId?: string | null;
  live?: boolean;
}) {
  const id = options.sessionId.trim();
  if (!id) return;
  if (options.messages?.length) {
    saveChatMessages(id, options.messages);
  }
  const alreadyListed = listChatHistory().some((item) => item.id === id);
  if (alreadyListed) {
    patchChatHistory(id, {
      messages: options.messages,
      fallbackTitle: options.fallbackTitle,
      agentName: options.agentName,
      workflowId: options.workflowId,
    });
  } else {
    promoteChatHistory(options);
  }
  if (options.live !== false) setChatLive(id, true);
}

/**
 * Create or move a chat to the top.
 * Only call for new chats or when the user sends a message.
 */
export function promoteChatHistory(options: {
  sessionId: string;
  messages?: BuilderMessage[];
  fallbackTitle?: string;
  agentName?: string | null;
  workflowId?: string | null;
}) {
  if (typeof window === "undefined") return;
  const id = options.sessionId.trim();
  if (!id) return;

  if (options.messages?.length) {
    saveChatMessages(id, options.messages);
  }

  const existing = readIndex().find((item) => item.id === id);
  if (existing?.titleLocked) {
    const workflowId = options.workflowId?.trim() || existing.workflowId;
    writeIndex([
      {
        ...existing,
        updatedAt: new Date().toISOString(),
        ...(workflowId ? { workflowId } : {}),
      },
      ...readIndex().filter((item) => item.id !== id),
    ]);
    return;
  }
  const computed = options.messages
    ? titleFromMessages(options.messages, options.fallbackTitle)
    : options.fallbackTitle?.trim() || existing?.title || "New chat";
  // v2: keep a non-generic title (first message / rename / agent) once set.
  const title =
    existing && !isPlaceholder(existing.title) ? existing.title : computed || "New chat";

  const agentName =
    options.agentName && !isPlaceholder(options.agentName)
      ? options.agentName.trim()
      : existing?.agentName;
  const workflowId = options.workflowId?.trim() || existing?.workflowId;

  const next: ChatHistoryItem = {
    id,
    title,
    updatedAt: new Date().toISOString(),
    ...(existing?.pinned ? { pinned: true } : {}),
    ...(agentName ? { agentName } : {}),
    ...(workflowId ? { workflowId } : {}),
  };

  writeIndex([next, ...readIndex().filter((item) => item.id !== id)]);
}

export function removeChatHistory(sessionId: string) {
  if (typeof window === "undefined") return;
  const id = sessionId.trim();
  abortActiveWorkflowStream(id);
  const timer = saveTimers.get(id);
  if (timer !== undefined) {
    window.clearTimeout(timer);
    saveTimers.delete(id);
  }
  clearLiveBuffer(id);
  deleteBuildChat(id);
  try {
    window.localStorage.removeItem(chatStorageKey(id));
  } catch {
    // ignore
  }
  setChatStreaming(id, false);
  writeIndex(readIndex().filter((item) => item.id !== id));
  // FlowOps session route is GET-only — deletion is local index only.
}

export function setChatPinned(sessionId: string, pinned: boolean) {
  if (typeof window === "undefined") return;
  const id = sessionId.trim();
  if (!id) return;
  writeIndex(
    sortChatHistory(readIndex().map((item) => (item.id === id ? { ...item, pinned } : item))),
  );
  // FlowOps session route is GET-only — pin is local index only.
}

export function renameChatHistory(sessionId: string, title: string) {
  if (typeof window === "undefined") return;
  const id = sessionId.trim();
  const nextTitle = title.trim().slice(0, 80);
  if (!id || !nextTitle) return;
  writeIndex(
    readIndex().map((item) =>
      item.id === id
        ? { ...item, title: nextTitle, agentName: undefined, titleLocked: true }
        : item,
    ),
  );
  // FlowOps session route is GET-only — rename is local index only.
}

export function subscribeChatHistory(onChange: () => void) {
  if (typeof window === "undefined") return () => {};
  const unsubscribe = onChatsChanged(onChange);
  window.addEventListener("storage", onChange);
  return () => {
    unsubscribe();
    window.removeEventListener("storage", onChange);
  };
}

/** Live badge — prefer applyChatFrame for stream lifecycle; use for restore / optimistic send. */
export function setChatLive(sessionId: string, live: boolean) {
  const id = sessionId.trim();
  if (!id || typeof window === "undefined") return;
  setChatStreaming(id, live);
}

export function isChatLive(sessionId: string) {
  return isChatStreaming(sessionId.trim());
}

/** The agent parked this chat on a question or form and is waiting on the user. */
export function isChatWaitingOnUser(sessionId: string) {
  return isChatAwaitingInput(sessionId.trim());
}

/** Test helper — clear debounce timers + memory between cases. */
export function resetChatHistoryMemoryForTests() {
  for (const timer of saveTimers.values()) window.clearTimeout(timer);
  saveTimers.clear();
  resetLiveStoreForTests();
  resetBuildChatStorageForTests();
}
